import React, { useState, useRef, useEffect } from 'react';
import { infoIcon } from '../../assets/icons';
import styles from './infoPopup.module.css';

interface InfoPopupProps {
  /** The information text to display in the popup */
  content: string;
  /** Position of the popup relative to the trigger button */
  position?: 'top' | 'bottom' | 'left' | 'right';
  /** Custom CSS class for styling */
  className?: string;
  /** Whether to show the info icon (default: true) */
  showIcon?: boolean;
  /** Custom trigger element (if not provided, uses default info icon) */
  trigger?: React.ReactNode;
  /** ARIA label for the info button */
  ariaLabel?: string;
}

export default function InfoPopup({
  content,
  position = 'top',
  className = '',
  showIcon = true,
  trigger,
  ariaLabel = 'More information'
}: InfoPopupProps) {  const [isVisible, setIsVisible] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<number | null>(null);

  const showPopup = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setIsVisible(true);
  };
  
  const hidePopup = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 150); // Small delay to prevent flickering when moving between trigger and popup
  };

  const cancelHidePopup = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  const calculatePosition = () => {
    if (!triggerRef.current || !popupRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const popupRect = popupRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let top = 0;
    let left = 0;

    switch (position) {
      case 'top':
        top = triggerRect.top - popupRect.height - 10;
        left = triggerRect.left + triggerRect.width / 2 - popupRect.width / 2;
        break;
      case 'bottom':
        top = triggerRect.bottom + 10;
        left = triggerRect.left + triggerRect.width / 2 - popupRect.width / 2;
        break;
      case 'left':
        top = triggerRect.top + triggerRect.height / 2 - popupRect.height / 2;
        left = triggerRect.left - popupRect.width - 10;
        break;
      case 'right':
        top = triggerRect.top + triggerRect.height / 2 - popupRect.height / 2;
        left = triggerRect.right + 10;
        break;
    }

    // Ensure popup stays within viewport
    if (left < 10) left = 10;
    if (left + popupRect.width > viewportWidth - 10) {
      left = viewportWidth - popupRect.width - 10;
    }
    if (top < 10) top = 10;
    if (top + popupRect.height > viewportHeight - 10) {
      top = viewportHeight - popupRect.height - 10;
    }

    setPopupPosition({ top, left });
  };

  useEffect(() => {
    if (isVisible) {
      calculatePosition();
    }
  }, [isVisible, position]);
  useEffect(() => {
    const handleResize = () => {
      if (isVisible) {
        calculatePosition();
      }
    };

    const handleScroll = () => {
      if (isVisible) {
        calculatePosition();
      }
    };

    if (isVisible) {
      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleScroll);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, [isVisible]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      hidePopup();
    }
  };
  return (
    <span className={`${styles.infoPopup} ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        onMouseEnter={showPopup}
        onMouseLeave={hidePopup}
        onFocus={showPopup}
        onBlur={hidePopup}
        onKeyDown={handleKeyDown}
        aria-label={ariaLabel}
        aria-expanded={isVisible}
        aria-describedby={isVisible ? 'info-popup-content' : undefined}
      >
        {trigger || (showIcon && <i aria-hidden="true">{infoIcon}</i>)}
        {!trigger && !showIcon && <span>Information</span>}
      </button>
      
      {isVisible && (
        <div
          ref={popupRef}
          id="info-popup-content"
          className={`${styles.popup} ${styles[position]}`}
          style={{
            position: 'fixed',
            top: `${popupPosition.top}px`,
            left: `${popupPosition.left}px`,
            zIndex: 1000
          }}
          role="tooltip"
          aria-live="polite"
          onMouseEnter={cancelHidePopup}
          onMouseLeave={hidePopup}
        >
          <div className={styles.content}>
            {content}
          </div>
        </div>
      )}
    </span>
  );
}
